/**
 * How ingredients relate to each other, and how much of one you get from another.
 *
 * The vocabulary says what a thing IS. This says what having one thing means for
 * needing another — which is the difference between "limes and lime juice share
 * a word" and "a lime gives you about two tablespoons of juice, you have five,
 * you are fine".
 *
 * Four relations, deliberately, and only two of them do arithmetic:
 *
 *   derives-from   limes -> lime juice. Carries a `yield`, so coverage can be
 *                  computed rather than guessed. This is the one the app was
 *                  missing when it told you to buy lime juice with five limes
 *                  in the fridge. ONLY for transformations a cook can do
 *                  tonight — see the note where the list ends.
 *   substitutes-for sherry <-> Shaoxing wine. Carries a `ratio` for the sheet.
 *   part-of        egg -> yolk. NO arithmetic: three yolks from a dozen eggs is
 *                  obvious to a cook and not worth a wrong number, so this
 *                  resolves to `probably` and asks.
 *   form-of        whole chicken -> breasts. Also no arithmetic, same reason.
 *
 * Adding a yield: measure it, do not estimate it. A wrong yield is worse than no
 * yield, because no yield asks the user and a wrong one does not.
 */

import type { CanonicalIngredientId } from '../utils/ingredientVocabulary';

export type RelationType = 'derives-from' | 'part-of' | 'form-of' | 'substitutes-for';

export interface IngredientYield {
  /** How much you get. */
  amount: number;
  /** The unit you get it in. */
  unit: string;
  /** Per how many of the source. Almost always 1. */
  per: number;
}

export interface IngredientRelation {
  /** What you have. */
  from: CanonicalIngredientId;
  /** What the recipe wants. */
  to: CanonicalIngredientId;
  type: RelationType;
  /**
   * Only on `derives-from`, and effectively required there: a derivation a cook
   * can actually perform has a measurable yield. See the note at the end of the
   * list for why a yield-less derivation does not belong in this file at all.
   */
  yield?: IngredientYield;
  /** Only on `substitutes-for`. Free text, shown in the substitution sheet. */
  ratio?: string;
}

/**
 * Citrus yields are the median of a supermarket fruit, not a perfect one, and
 * deliberately conservative — telling someone they have enough juice when they
 * do not is the failure that ruins a dinner.
 */
export const INGREDIENT_RELATIONS: IngredientRelation[] = [
  // -- derives-from, with measurable yields ---------------------------------
  { from: 'lime', to: 'lime-juice', type: 'derives-from', yield: { amount: 2, unit: 'tablespoon', per: 1 } },
  { from: 'lime', to: 'lime-zest', type: 'derives-from', yield: { amount: 2, unit: 'teaspoon', per: 1 } },
  { from: 'lemon', to: 'lemon-juice', type: 'derives-from', yield: { amount: 3, unit: 'tablespoon', per: 1 } },
  { from: 'lemon', to: 'lemon-zest', type: 'derives-from', yield: { amount: 1, unit: 'tablespoon', per: 1 } },
  { from: 'orange', to: 'orange-juice', type: 'derives-from', yield: { amount: 5, unit: 'tablespoon', per: 1 } },
  { from: 'orange', to: 'orange-zest', type: 'derives-from', yield: { amount: 2, unit: 'tablespoon', per: 1 } },
  { from: 'apple', to: 'apple-juice', type: 'derives-from', yield: { amount: 0.5, unit: 'cup', per: 1 } },

  // NOT here, deliberately: tomato -> tomato paste, garlic -> garlic powder,
  // onion -> onion powder, ginger -> ground ginger, mushroom -> dried
  // mushroom.
  //
  // Those are real transformations, but not ones a cook does mid-recipe. They
  // were listed as yield-less `derives-from` on the theory that "cannot compute
  // the amount, so ask" — which produced the question "Vous avez 4 Tomates en
  // grappe. Assez ?" on a line calling for a tablespoon of tomato paste. Nobody
  // reduces four fresh tomatoes into a tablespoon of paste, and asking implies
  // they might.
  //
  // The rule this encodes: a relation belongs here only if the cook can get
  // from `from` to `to` in their own kitchen, tonight. Squeezing a lime and
  // separating an egg qualify; dehydrating garlic does not. The identity link
  // between a food and its forms still exists — it lives on `base` in
  // ingredientVocabulary.ts, which is what lets "tomato paste" borrow tomato's
  // substitutes and what promotes "peel from 1 lemon" to lemon zest. That is
  // about what a thing IS. This file is about what covers what.

  // -- part-of: a component of the whole thing ------------------------------
  { from: 'egg', to: 'egg-yolk', type: 'part-of' },
  { from: 'egg', to: 'egg-white', type: 'part-of' },

  // -- form-of: the same animal or plant, butchered differently -------------
  { from: 'whole-chicken', to: 'chicken-breast', type: 'form-of' },
  { from: 'whole-chicken', to: 'chicken-thigh', type: 'form-of' },
  { from: 'whole-chicken', to: 'chicken', type: 'form-of' },
  { from: 'chicken', to: 'chicken-breast', type: 'form-of' },
  { from: 'chicken', to: 'chicken-thigh', type: 'form-of' },
  { from: 'beef', to: 'ground-beef', type: 'form-of' },
  { from: 'pork', to: 'ground-pork', type: 'form-of' },
  { from: 'canned-tomatoes', to: 'tomato-sauce', type: 'form-of' },

  // -- substitutes-for: the pair the reported bug was about, and its kin ----
  { from: 'sherry', to: 'rice-wine', type: 'substitutes-for', ratio: '1:1' },
  { from: 'rice-wine', to: 'sherry', type: 'substitutes-for', ratio: '1:1' },
  { from: 'vermouth', to: 'rice-wine', type: 'substitutes-for', ratio: '1:1' },
  { from: 'vermouth', to: 'sherry', type: 'substitutes-for', ratio: '1:1' },
  { from: 'white-wine', to: 'sherry', type: 'substitutes-for', ratio: '1:1' },
  { from: 'lemon-juice', to: 'lime-juice', type: 'substitutes-for', ratio: '1:1' },
  { from: 'lime-juice', to: 'lemon-juice', type: 'substitutes-for', ratio: '1:1' },
  { from: 'greek-yogurt', to: 'sour-cream', type: 'substitutes-for', ratio: '1:1' },
  { from: 'sour-cream', to: 'greek-yogurt', type: 'substitutes-for', ratio: '1:1' },
  { from: 'creme-fraiche', to: 'sour-cream', type: 'substitutes-for', ratio: '1:1' },
  { from: 'scallion', to: 'chives', type: 'substitutes-for', ratio: '1:1' },
  { from: 'cilantro', to: 'parsley', type: 'substitutes-for', ratio: '1:1' },
];

/** Relations indexed by what the recipe wants, built once. */
const BY_TARGET = new Map<CanonicalIngredientId, IngredientRelation[]>();
for (const relation of INGREDIENT_RELATIONS) {
  const bucket = BY_TARGET.get(relation.to);
  if (bucket) bucket.push(relation);
  else BY_TARGET.set(relation.to, [relation]);
}

/** Everything that could cover `id`, best relation first. */
export function relationsTo(id: CanonicalIngredientId): IngredientRelation[] {
  return BY_TARGET.get(id) ?? [];
}
